import { useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  GET_SUBSCRIPTION,
  GET_PLANS,
  CANCEL_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  EXTEND_SUBSCRIPTION,
  UPGRADE_SUBSCRIPTION,
  DOWNGRADE_SUBSCRIPTION,
} from './subscription.queries';
import {
  AdminUserSubscription,
  AdminSubscriptionStatus,
  SubscriptionPayment,
  SubscriptionHistory,
  AdminPlan,
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_COLORS,
  BILLING_PERIOD_LABELS,
  formatPrice,
  formatDate,
  formatDateTime,
} from './subscription.types';

// ============================================================================
// CANCEL DIALOG
// ============================================================================

interface CancelDialogProps {
  open: boolean;
  subscription: AdminUserSubscription | null;
  onClose: () => void;
  onConfirm: (reason: string, immediate: boolean) => void;
  loading: boolean;
}

function CancelDialog({ open, subscription: _subscription, onClose, onConfirm, loading }: CancelDialogProps) {
  const [reason, setReason] = useState('');
  const [immediate, setImmediate] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>
        Annuler l'abonnement
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Cette action est irréversible. L'utilisateur perdra l'accès aux fonctionnalités premium.
        </Alert>
        <Stack spacing={2}>
          <TextField
            label="Raison de l'annulation"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Entrez la raison de l'annulation..."
          />
          <FormControl fullWidth>
            <InputLabel>Type d'annulation</InputLabel>
            <Select
              value={immediate ? 'immediate' : 'end_of_period'}
              label="Type d'annulation"
              onChange={(e) => setImmediate(e.target.value === 'immediate')}
            >
              <MenuItem value="end_of_period">À la fin de la période de facturation</MenuItem>
              <MenuItem value="immediate">Immédiatement</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={() => onConfirm(reason, immediate)}
          color="error"
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Annulation...' : 'Confirmer l\'annulation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// EXTEND DIALOG
// ============================================================================

interface ExtendDialogProps {
  open: boolean;
  subscription: AdminUserSubscription | null;
  onClose: () => void;
  onConfirm: (days: number, reason: string) => void;
  loading: boolean;
}

function ExtendDialog({ open, subscription, onClose, onConfirm, loading }: ExtendDialogProps) {
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Prolonger l'abonnement</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Date de fin actuelle: <strong>{subscription ? formatDate(subscription.endDate) : '-'}</strong>
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Nombre de jours"
            type="number"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Raison"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Ex: Compensation pour problème technique..."
          />
          {days > 0 && subscription && (
            <Alert severity="info">
              Nouvelle date de fin: {formatDate(new Date(new Date(subscription.endDate).getTime() + days * 24 * 60 * 60 * 1000))}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={() => onConfirm(days, reason)}
          variant="contained"
          disabled={loading || days <= 0}
        >
          {loading ? 'Prolongation...' : `Prolonger de ${days} jours`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// CHANGE PLAN DIALOG
// ============================================================================

interface ChangePlanDialogProps {
  open: boolean;
  subscription: AdminUserSubscription | null;
  plans: AdminPlan[];
  onClose: () => void;
  onConfirm: (planCode: string, immediate: boolean) => void;
  loading: boolean;
}

function ChangePlanDialog({ open, subscription, plans, onClose, onConfirm, loading }: ChangePlanDialogProps) {
  const [selectedPlanCode, setSelectedPlanCode] = useState('');
  const [immediate, setImmediate] = useState(false);

  const currentPlan = subscription?.plan;
  const availablePlans = plans.filter((p) => p.code !== currentPlan?.code);
  const selectedPlan = plans.find((p) => p.code === selectedPlanCode);
  const isUpgrade = selectedPlan && currentPlan && selectedPlan.monthlyPrice > (currentPlan.monthlyPrice || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Changer de forfait
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Forfait actuel: <strong>{currentPlan?.name}</strong> ({formatPrice(currentPlan?.monthlyPrice || 0)}/mois)
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Nouveau forfait</InputLabel>
            <Select
              value={selectedPlanCode}
              label="Nouveau forfait"
              onChange={(e) => setSelectedPlanCode(e.target.value)}
            >
              {availablePlans.map((plan) => (
                <MenuItem key={plan.code} value={plan.code}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span>{plan.name}</span>
                    <Typography variant="body2" color="text.secondary">
                      {formatPrice(plan.monthlyPrice)}/mois
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedPlan && (
            <>
              <Alert severity={isUpgrade ? 'info' : 'warning'}>
                {isUpgrade
                  ? `Upgrade vers ${selectedPlan.name} (+${formatPrice(selectedPlan.monthlyPrice - (currentPlan?.monthlyPrice || 0))}/mois)`
                  : `Downgrade vers ${selectedPlan.name} (-${formatPrice((currentPlan?.monthlyPrice || 0) - selectedPlan.monthlyPrice)}/mois)`}
              </Alert>

              <FormControl fullWidth>
                <InputLabel>Application</InputLabel>
                <Select
                  value={immediate ? 'immediate' : 'next_billing'}
                  label="Application"
                  onChange={(e) => setImmediate(e.target.value === 'immediate')}
                >
                  <MenuItem value="immediate">Immédiatement</MenuItem>
                  <MenuItem value="next_billing">À la prochaine facturation</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={() => onConfirm(selectedPlanCode, immediate)}
          variant="contained"
          color={isUpgrade ? 'primary' : 'warning'}
          disabled={loading || !selectedPlanCode}
        >
          {loading ? 'En cours...' : isUpgrade ? 'Upgrade' : 'Downgrade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// PAYMENT STATUS CHIP
// ============================================================================

function PaymentStatusChip({ status }: { status: string }) {
  const config: Record<string, { color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactNode }> = {
    COMPLETED: { color: 'success', icon: <CheckCircleIcon /> },
    PAID: { color: 'success', icon: <CheckCircleIcon /> },
    PENDING: { color: 'warning', icon: <PendingIcon /> },
    FAILED: { color: 'error', icon: <ErrorIcon /> },
    REFUNDED: { color: 'default', icon: <RefreshIcon /> },
  };
  const { color, icon } = config[status] || { color: 'default' as const, icon: null };

  return (
    <Chip
      icon={icon as React.ReactElement}
      label={status}
      size="small"
      color={color}
    />
  );
}

// ============================================================================
// INFO CARD
// ============================================================================

interface InfoCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function InfoCard({ title, icon, children }: InfoCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          {title}
        </Typography>
        <Divider sx={{ my: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SubscriptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  // Dialogs state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_SUBSCRIPTION, {
    variables: { id },
    skip: !id,
  });
  const { data: plansData } = useQuery(GET_PLANS, { variables: { activeOnly: true } });

  // Mutations
  const [cancelSubscription, { loading: cancelling }] = useMutation(CANCEL_SUBSCRIPTION);
  const [reactivateSubscription, { loading: reactivating }] = useMutation(REACTIVATE_SUBSCRIPTION);
  const [extendSubscription, { loading: extending }] = useMutation(EXTEND_SUBSCRIPTION);
  const [upgradeSubscription, { loading: upgrading }] = useMutation(UPGRADE_SUBSCRIPTION);
  const [downgradeSubscription, { loading: downgrading }] = useMutation(DOWNGRADE_SUBSCRIPTION);

  const subscription: AdminUserSubscription | null = data?.adminSubscription;
  const plans: AdminPlan[] = plansData?.adminPlans || [];

  // Handlers
  const handleCancel = async (reason: string, immediate: boolean) => {
    try {
      await cancelSubscription({ variables: { id, reason, immediate } });
      notify('Abonnement annulé', { type: 'success' });
      setCancelDialogOpen(false);
      refetch();
    } catch {
      notify('Erreur lors de l\'annulation', { type: 'error' });
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateSubscription({ variables: { id } });
      notify('Abonnement réactivé', { type: 'success' });
      refetch();
    } catch {
      notify('Erreur lors de la réactivation', { type: 'error' });
    }
  };

  const handleExtend = async (days: number, reason: string) => {
    try {
      await extendSubscription({ variables: { id, days, reason } });
      notify(`Abonnement prolongé de ${days} jours`, { type: 'success' });
      setExtendDialogOpen(false);
      refetch();
    } catch {
      notify('Erreur lors de la prolongation', { type: 'error' });
    }
  };

  const handleChangePlan = async (planCode: string, immediate: boolean) => {
    const currentPrice = subscription?.plan?.monthlyPrice || 0;
    const newPlan = plans.find((p) => p.code === planCode);
    const isUpgrade = newPlan && newPlan.monthlyPrice > currentPrice;

    try {
      if (isUpgrade) {
        await upgradeSubscription({ variables: { id, toPlanCode: planCode, immediate } });
      } else {
        await downgradeSubscription({ variables: { id, toPlanCode: planCode } });
      }
      notify(`Forfait changé vers ${newPlan?.name}`, { type: 'success' });
      setChangePlanDialogOpen(false);
      refetch();
    } catch {
      notify('Erreur lors du changement de forfait', { type: 'error' });
    }
  };

  // Loading
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  // Error
  if (error || !subscription) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error?.message || 'Abonnement non trouvé'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/subscriptions')} sx={{ mt: 2 }}>
          Retour aux abonnements
        </Button>
      </Box>
    );
  }

  const statusColor = SUBSCRIPTION_STATUS_COLORS[subscription.status];
  const isActive = subscription.status === AdminSubscriptionStatus.ACTIVE || subscription.status === AdminSubscriptionStatus.TRIAL;
  const isCancelled = subscription.status === AdminSubscriptionStatus.CANCELLED;

  // Calculate days remaining
  const daysRemaining = Math.ceil((new Date(subscription.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <Box sx={{ p: 3 }}>
      <Title title={`Abonnement: ${subscription.user?.email || subscription.enterprise?.name || id}`} />

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/subscriptions')}
          sx={{ mb: 2 }}
        >
          Retour aux abonnements
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {subscription.user?.email || subscription.enterprise?.name || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Chip
                label={SUBSCRIPTION_STATUS_LABELS[subscription.status]}
                sx={{
                  backgroundColor: `${statusColor}20`,
                  color: statusColor,
                  fontWeight: 600,
                }}
              />
              <Chip
                label={subscription.plan?.name || 'N/A'}
                variant="outlined"
                sx={{ borderColor: subscription.plan?.colorPrimary }}
              />
              <Chip
                label={BILLING_PERIOD_LABELS[subscription.billingPeriod]}
                size="small"
              />
            </Box>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            {isActive && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<MoreTimeIcon />}
                  onClick={() => setExtendDialogOpen(true)}
                >
                  Prolonger
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UpgradeIcon />}
                  onClick={() => setChangePlanDialogOpen(true)}
                >
                  Changer forfait
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setCancelDialogOpen(true)}
                >
                  Annuler
                </Button>
              </>
            )}
            {isCancelled && (
              <Button
                variant="contained"
                color="success"
                startIcon={<RestoreIcon />}
                onClick={handleReactivate}
                disabled={reactivating}
              >
                {reactivating ? 'Réactivation...' : 'Réactiver'}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Info Cards */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Subscriber Info */}
            <Grid item xs={12} md={6}>
              <InfoCard
                title={subscription.enterpriseId ? 'Entreprise' : 'Utilisateur'}
                icon={subscription.enterpriseId ? <BusinessIcon color="primary" /> : <PersonIcon color="primary" />}
              >
                <Stack spacing={1}>
                  {subscription.user && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Email</Typography>
                      <Typography fontWeight={500}>{subscription.user.email}</Typography>
                    </Box>
                  )}
                  {subscription.enterprise && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Entreprise</Typography>
                      <Typography fontWeight={500}>{subscription.enterprise.name}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">ID</Typography>
                    <Typography variant="caption" fontFamily="monospace">
                      {subscription.userId || subscription.enterpriseId}
                    </Typography>
                  </Box>
                </Stack>
              </InfoCard>
            </Grid>

            {/* Subscription Info */}
            <Grid item xs={12} md={6}>
              <InfoCard title="Abonnement" icon={<CalendarMonthIcon color="primary" />}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Créé le</Typography>
                    <Typography>{formatDate(subscription.createdAt)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Date de début</Typography>
                    <Typography>{formatDate(subscription.startDate)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Date de fin</Typography>
                    <Typography fontWeight={500}>{formatDate(subscription.endDate)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Jours restants</Typography>
                    <Chip
                      label={daysRemaining > 0 ? `${daysRemaining} jours` : 'Expiré'}
                      size="small"
                      color={daysRemaining > 7 ? 'success' : daysRemaining > 0 ? 'warning' : 'error'}
                    />
                  </Box>
                  {subscription.trialEndDate && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Fin d'essai</Typography>
                      <Typography>{formatDate(subscription.trialEndDate)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Renouvellement auto</Typography>
                    <Chip
                      label={subscription.autoRenew ? 'Oui' : 'Non'}
                      size="small"
                      color={subscription.autoRenew ? 'success' : 'default'}
                    />
                  </Box>
                </Stack>
              </InfoCard>
            </Grid>

            {/* Billing Info */}
            <Grid item xs={12}>
              <InfoCard title="Facturation" icon={<PaymentIcon color="primary" />}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Montant</Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {formatPrice(subscription.priceAmount, subscription.priceCurrency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Période</Typography>
                    <Typography variant="h6">
                      {BILLING_PERIOD_LABELS[subscription.billingPeriod]}
                    </Typography>
                  </Grid>
                  {subscription.discountPercent && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Réduction</Typography>
                      <Typography variant="h6" color="success.main">
                        -{subscription.discountPercent}%
                      </Typography>
                    </Grid>
                  )}
                  {subscription.discountCode && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Code promo</Typography>
                      <Typography variant="h6">
                        <Chip label={subscription.discountCode} size="small" />
                      </Typography>
                    </Grid>
                  )}
                  {subscription.paymentMethod && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Méthode</Typography>
                      <Typography>{subscription.paymentMethod}</Typography>
                    </Grid>
                  )}
                  {subscription.nextBillingDate && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Prochaine facturation</Typography>
                      <Typography>{formatDate(subscription.nextBillingDate)}</Typography>
                    </Grid>
                  )}
                </Grid>
              </InfoCard>
            </Grid>

            {/* Payment History */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon color="primary" />
                    Historique des paiements
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  {subscription.payments && subscription.payments.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Montant</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Transaction ID</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {subscription.payments.map((payment: SubscriptionPayment) => (
                            <TableRow key={payment.id} hover>
                              <TableCell>
                                {payment.paidAt ? formatDateTime(payment.paidAt) : formatDateTime(payment.createdAt)}
                              </TableCell>
                              <TableCell>
                                <Typography fontWeight={500}>
                                  {formatPrice(payment.amount, payment.currency)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <PaymentStatusChip status={payment.status} />
                              </TableCell>
                              <TableCell>{payment.provider}</TableCell>
                              <TableCell>
                                <Typography variant="caption" fontFamily="monospace">
                                  {payment.providerTxnId || '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Aucun paiement enregistré
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - History Timeline */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                Historique des actions
              </Typography>
              <Divider sx={{ my: 2 }} />
              {subscription.history && subscription.history.length > 0 ? (
                <List dense sx={{ p: 0 }}>
                  {subscription.history.map((event: SubscriptionHistory) => {
                    const dotColor =
                      event.toStatus === 'ACTIVE' ? 'success.main' :
                      event.toStatus === 'CANCELLED' ? 'error.main' :
                      event.toStatus === 'TRIAL' ? 'info.main' : 'grey.500';
                    return (
                      <ListItem
                        key={event.id}
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          borderLeft: '2px solid',
                          borderColor: 'divider',
                          ml: 1,
                          pl: 2,
                          pb: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <FiberManualRecordIcon sx={{ fontSize: 12, color: dotColor, ml: -2.7 }} />
                          <Typography variant="body2" fontWeight={500}>
                            {event.action}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0 }}>
                          {formatDateTime(event.createdAt)}
                        </Typography>
                        {event.fromStatus && event.toStatus && (
                          <Typography variant="caption" color="text.secondary">
                            {event.fromStatus} → {event.toStatus}
                          </Typography>
                        )}
                        {event.reason && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Raison: {event.reason}
                          </Typography>
                        )}
                        {event.performedBy && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Par: {event.performedBy}
                          </Typography>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Aucun historique
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <CancelDialog
        open={cancelDialogOpen}
        subscription={subscription}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancel}
        loading={cancelling}
      />
      <ExtendDialog
        open={extendDialogOpen}
        subscription={subscription}
        onClose={() => setExtendDialogOpen(false)}
        onConfirm={handleExtend}
        loading={extending}
      />
      <ChangePlanDialog
        open={changePlanDialogOpen}
        subscription={subscription}
        plans={plans}
        onClose={() => setChangePlanDialogOpen(false)}
        onConfirm={handleChangePlan}
        loading={upgrading || downgrading}
      />
    </Box>
  );
}

export default SubscriptionDetail;
